// Global Instructions Rule Applied!
import { useCallback, useEffect, useState } from 'react'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import {
  AgentDraftRecord, OUTLOOK_CAPABILITY_OFF, OutlookDraftCapability
} from '../../../state_manager/data_models/agent_drafts_model.ts'
import { fetchAgentDrafts, fetchMyAgentDrafts } from '../../internal_agents/services/agentDraftsService.ts'

// 'exported' is OPEN work: the item sits unsent in Outlook until 6b links the
// send (or the user acts), so it stays on the inbox beside proposed drafts.
export const AGENT_DRAFT_OPEN_STATUSES = ['proposed', 'exported']
const OPEN_ONLY = AGENT_DRAFT_OPEN_STATUSES
const WITH_HISTORY = ['proposed', 'exported', 'sent', 'accepted', 'dismissed', 'superseded', 'expired']

// Drafts for one account/prospect context, or (contextId null + mine) the
// caller's My Drafts inbox.
export function useAgentDrafts(
  context: { contextTable: 'accounts' | 'prospects'; contextId: string | null | undefined } | { mine: true },
  options: { includeHistory?: boolean } = {}
) {
  const { includeHistory = false } = options
  const [drafts, setDrafts] = useState<AgentDraftRecord[]>([])
  const [outlookCapability, setOutlookCapability] = useState<OutlookDraftCapability>(OUTLOOK_CAPABILITY_OFF)
  const [loading, setLoading] = useState(false)
  const isMine = 'mine' in context
  const contextTable = isMine ? null : context.contextTable
  const contextId = isMine ? null : context.contextId

  const refetch = useCallback(async () => {
    if (!isMine && !contextId) {
      setDrafts([])
      return
    }
    setLoading(true)
    try {
      const token = getAccessToken(store.getState().auth)
      const status = includeHistory ? WITH_HISTORY : OPEN_ONLY
      const page = isMine
        ? await fetchMyAgentDrafts(status, token)
        : await fetchAgentDrafts(contextTable as 'accounts' | 'prospects', contextId as string, status, token)
      setDrafts(page.drafts)
      setOutlookCapability(page.outlookCapability)
    } catch (e) {
      showErrorToast(e instanceof Error ? e.message : 'Failed to load drafts')
    } finally {
      setLoading(false)
    }
  }, [isMine, contextTable, contextId, includeHistory])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { drafts, loading, refetch, outlookCapability }
}
